import { instance, loginInstance, ticketingInstance, reportInstance } from "../utilites/ApiClient";

/**
 * Generic HTTP request handler with error handling
 * @param {Object} axiosInstance - Axios instance to use
 * @param {string} method - HTTP method
 * @param {string} url - Endpoint URL
 * @param {any} data - Request data
 * @param {Object} options - Additional options
 */
const makeRequest = async (axiosInstance, method, url, data, options = {}) => {
  try {
    const { raw = false } = options;
    let response;

    switch (method) {
      case 'GET':
        response = await axiosInstance.get(url, data);
        break;
      case 'POST':
        response = await axiosInstance.post(url, data);
        break;
      case 'PUT':
        response = await axiosInstance.put(url, data);
        break;
      case 'DELETE':
        response = await axiosInstance.delete(url, { data });
        break;
      case 'PATCH':
        response = await axiosInstance.patch(url, raw ? data : { data });
        break;
      default:
        throw new Error(`Unsupported HTTP method: ${method}`);
    }

    return response;
  } catch (error) {
    if (error.response && error.response.status === 401) {
      window.location.reload();
    }
    return error;
  }
};

/**
 * Factory function to create a service object for any axios instance
 * @param {Object} axiosInstance - The axios instance to bind to this service
 * @returns {Object} Service object with HTTP methods
 */
const createService = (axiosInstance) => ({
  get: async (url, config) => makeRequest(axiosInstance, 'GET', url, config),
  post: async (url, data) => makeRequest(axiosInstance, 'POST', url, data),
  put: async (url, data) => makeRequest(axiosInstance, 'PUT', url, data),
  delete: async (url, data) => makeRequest(axiosInstance, 'DELETE', url, data),
  patch: async (url, data) => makeRequest(axiosInstance, 'PATCH', url, data),
  patchRaw: async (url, data) => makeRequest(axiosInstance, 'PATCH', url, data, { raw: true }),
  getDetail: async (url, id) => makeRequest(axiosInstance, 'GET', url, id),
});

// ==================== Service Objects ====================
// Each service object provides all HTTP methods for its instance

/**
 * Main API service (uses default instance)
 * Usage: api.get('/users'), api.post('/users', data)
 */
export const api = createService(instance);

/**
 * Ticketing API service (uses ticketingInstance)
 * Usage: ticketApi.get('/tickets'), ticketApi.post('/tickets', data)
 */
export const ticketApi = createService(ticketingInstance);

/**
 * Login/Auth API service (uses loginInstance)
 * Usage: authApi.post('/login', credentials)
 */
export const authApi = createService(loginInstance);

/**
 * Report API service (uses reportInstance)
 * Usage: reportApi.get('/reports'), reportApi.post('/reports', data)
 */
export const reportApi = createService(reportInstance);

// ==================== Special Request Handlers ====================

/**
 * Special handler for file upload with multipart/form-data
 */
export const uploadFile = async (url, request) => {
  try {
    instance.defaults.headers.common['Content-Type'] = 'multipart/form-data';
    const response = await instance.post(url, request['data']);
    return response;
  } catch (error) {
    if (error.response && error.response.status === 401) {
      window.location.reload();
    }
    return error;
  }
};

/**
 * Special handler for ticket escalation rule deletion
 * TODO: Refactor API to use standard DELETE format
 */
export const deleteEscalationRule = async (url, request) => {
  try {
    const response = await ticketingInstance.delete(url + "/" + request.id);
    return response;
  } catch (error) {
    if (error.response && error.response.status === 401) {
      window.location.reload();
    }
    return error;
  }
};

// ==================== Backward Compatibility Layer ====================
// These are deprecated but kept for existing code
// TODO: Gradually migrate to using api/ticketApi/authApi objects

export const getRequest = api.get;
export const postRequest = api.post;
export const putRequest = api.put;
export const deleteRequest = api.delete;
export const patchRequest = api.patch;
export const getDetailRequest = api.getDetail;
export const patchRawRequest = api.patchRaw;

export const getLocalData = ticketApi.get;
export const postLocalRequest = ticketApi.post;
export const putLocalRequest = ticketApi.put;
export const localDeleteRequest = ticketApi.delete;
export const patchLocalRequest = ticketApi.patch;
export const getLocalDetailRequest = ticketApi.getDetail;

export const loginPostRequest = authApi.post;

export const postImportRequest = uploadFile;
export const deleteLocalEscalationRuleRequest = deleteEscalationRule;

// ==================== Migration Guide ====================
/*
RECOMMENDED USAGE (New Code):
  import { api, ticketApi, authApi } from './Service';

  // Main API calls
  const users = await api.get('/users');
  const user = await api.post('/users', userData);

  // Ticket API calls
  const tickets = await ticketApi.get('/tickets');
  const ticket = await ticketApi.post('/tickets', ticketData);

  // Auth API calls
  const response = await authApi.post('/login', credentials);

BACKWARD COMPATIBLE (Existing Code):
  import { getRequest, postLocalRequest } from './Service';

  const users = await getRequest('/users');
  const tickets = await postLocalRequest('/tickets', data);

ADDING NEW SERVICES:
  If you need a 4th service (e.g., analytics), just add:

  import { analyticsInstance } from '../utilites/ApiClient';
  export const analyticsApi = createService(analyticsInstance);

  // That's it! You get all methods automatically:
  analyticsApi.get('/metrics')
  analyticsApi.post('/events', data)
  // ... etc
*/
